import { Injectable, Inject, DOCUMENT } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { SeoData } from './seo.model';

export const SITE_URL = 'https://notionhabitheroes.com';

@Injectable({ providedIn: 'root' })
export class SeoService {

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  init(): void {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(e => {
      const seo = this.deepestChildData();
      this.apply(seo, (e as NavigationEnd).urlAfterRedirects);
    });

    // Belt-and-braces: apply immediately if the router has already navigated
    // (catches the first render during prerendering where NavigationEnd already fired).
    if (this.router.navigated) {
      const seo = this.deepestChildData();
      this.apply(seo, this.router.url);
    }
  }

  private deepestChildData(): SeoData | undefined {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return snapshot.data['seo'] as SeoData | undefined;
  }

  apply(seo: SeoData | undefined, url: string): void {
    if (!seo) {
      return;
    }

    // Canonical: strip query string and fragment, then emit exactly one trailing slash.
    // Netlify 301-redirects every directory path to its slash form (/pricing → /pricing/),
    // so canonicals must match the redirect destination.
    const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
    const canonical = (path === '' || path === '/' ? SITE_URL : SITE_URL + path) + '/';

    this.title.setTitle(seo.title);
    this.meta.updateTag({ name: 'description', content: seo.description });

    const ogImage = SITE_URL + '/' + (seo.ogImage ?? 'assets/images/og-default-v2.jpg').replace(/^\//, '');

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:image', content: ogImage });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });
    this.meta.updateTag({ name: 'twitter:image', content: ogImage });

    // Robots
    if (seo.noindex) {
      this.meta.updateTag({ name: 'robots', content: 'noindex' });
    } else {
      this.meta.removeTag('name="robots"');
    }

    // Canonical <link>
    let linkEl = this.doc.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = this.doc.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonical);

    // JSON-LD: remove previously injected blocks, then inject new ones
    const existing = this.doc.head.querySelectorAll('script[type="application/ld+json"][data-seo]');
    existing.forEach(el => el.parentNode?.removeChild(el));

    if (seo.jsonLd) {
      const nodes = Array.isArray(seo.jsonLd) ? seo.jsonLd : [seo.jsonLd];
      nodes.forEach(obj => {
        const script = this.doc.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo', '');
        script.textContent = JSON.stringify(obj);
        this.doc.head.appendChild(script);
      });
    }
  }
}
